import moment from 'moment';
import { calculateCommissionDue, calculateRebate, splitFullName } from './global';
import { isAdmin, userHasAnyRole } from './roleHelpers';
import { getProfileFromLocalStorage, getSalesRepOfSalesManager } from './session';

export const ROLE_SUPER_ADMIN = 'Super Admin';
export const ROLE_ADMIN = 'Admin';
export const ROLE_SALES = 'Sales';
export const ROLE_SALES_MANAGER = 'Sales Manager';
export const ROLE_SERVICE = 'Service';
export const ROLE_TECH = 'Tech';
export const ROLE_INSTALLER = 'Installer';
export const ROLE_INVENTORY_MANAGER = 'Inventory Manager';
export const ROLE_SALES_AND_INVENTORY_MANAGER = 'Sales and Inventory Manager';

export const APP_ROLES = [
  ROLE_SUPER_ADMIN,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_MANAGER,
  ROLE_SERVICE,
  ROLE_TECH,
  ROLE_INSTALLER,
  ROLE_INVENTORY_MANAGER,
  ROLE_SALES_AND_INVENTORY_MANAGER
];

export const ROLE_KEY_INSTALLER = ROLE_INSTALLER.toLowerCase();
export const ROLE_KEY_SERVICE = ROLE_SERVICE.toLowerCase();
export const ROLE_KEYS_SALES_GROUP = [ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER].map(r =>
  r.toLowerCase()
);

export const STAGING_CLIENT_NAME = 'Housing Services';
export const MICASA_CLIENT_NAME = 'Micasa Housing';
export const API_URL = process.env.REACT_APP_API_BASE_URL;
export const APP_URL = process.env.REACT_APP_URL;
export const BRAND_NAME = process.env.REACT_APP_CLIENT;
export const CLIENT_PHONE_NUMBER = process.env.REACT_APP_CLIENT_PHONE;
export const CLIENT_EMAIL = process.env.REACT_APP_CLIENT_EMAIL;
export const isStaging = BRAND_NAME === STAGING_CLIENT_NAME;
export const isMicasa = BRAND_NAME === MICASA_CLIENT_NAME;

export const UNAUTHORIZED_TEXT = 'You are not authorized to view this page';

export const DOCHUB_DATA_SOURCES = ['Contract', 'Contact', 'Mock', 'Inventory'];
/** Group of files the DocHub "Useful Files" explorer browses. */
export const DOCHUB_USEFUL_FILES_FOLDER = 'Dochub Important Files';
export const CONTRACTOR_CATEGORY_OPTIONS = ['Factory', 'Transport', 'Factory Transport', 'Contractor'];

// API RELATED CONSTANTS
export const ENDPOINTS = {
  //auth
  LOGIN: '/users/login',
  LOGOUT: '/users/logout',
  REGISTER: '/users/register',
  GENERATE_REGISTER_OTP: '/users/generate-registration-otp',
  FORGOT_PASSWORD: '/users/send-password-reset-link',
  VERIFY_EMAIL: '/users/verify-email',
  CHANGE_PASSWORD: '/users/change-password',
  RESET_PASSWORD: '/users/reset-password',
  //users
  USERS_BASE: '/users/',
  USERS_ME: '/users/me',
  USERS_WITH_ID: id => `/users/${id}`,
  USERS_LIST: '/users/list',
  USERS_UPDATE: '/users/update-user-details',
  //sales location
  SALES_LOCATION_BASE: '/sales-locations',
  SALES_LOCATION_LIST: '/sales-locations/list',
  SALES_LOCATION_WITH_ID: id => `/sales-locations/${id}`,
  //models
  MODELS_BASE: '/models',
  MODELS_LIST: '/models/list',
  MODELS_WITH_ID: id => `/models/${id}`,
  MODELS_BY_MANUFACTURER: manufacturerId => `/models/manufacturer/${manufacturerId}`,
  MODELS_BULK_UPDATE: '/models/bulk-update',
  //sites
  SITE_BASE: '/sites',
  SITE_LIST: '/sites/search',
  SITE_WITH_ID: id => `/sites/${id}`,
  //slots
  SLOT_BASE: '/slots',
  SLOT_LIST: '/slots/search',
  SLOT_WITH_ID: id => `/slots/${id}`,
  //contracts
  CONTRACTS_BASE: '/contracts',
  CONTRACTS_WITH_ID: id => `/contracts/${id}`,
  CONTRACTS_DELETE_COMMENT: id => `/contracts/${id}/delete-comment`,
  CONTRACTS_SEARCH: '/contracts/search',
  CONTRACTS_SEARCH_NEW: '/contracts/search/new',
  CONTRACTS_NOTIFICATION: '/contracts/notification',
  CONTRACTS_CHANGE_WALKTHROUGH_ACTION: (contractId, actionType) =>
    `/contracts/change-walkthrough-action/${contractId}/${actionType}`,
  CONTRACTS_APPROVE_WALKTHROUGH: uuid => `/contracts/approve-walkthrough/${uuid}`,
  CONTRACTS_DOWNLOAD_CSV: `/contracts/csv/contracted-buyers`,

  //contract reports
  REPORTS_GET_FORMT: '/contracts/form-t',
  REPORTS_GET_SOL: '/contracts/sol-report',
  REPORTS_SUBMIT_REBATE: '/contracts/rebate',
  REPORTS_SUBMIT_AC_REBATE: '/contracts/ac-rebate',
  //service requests:
  SERVICE_REQUEST_BASE: '/service-requests',
  SERVICE_REQUEST_LIST: '/service-requests/list',
  SERVICE_REQUEST_CLAIM: '/service-requests/claim',
  SERVICE_REQUEST_RESOLVE: '/service-requests/resolve',
  //contacts
  CONTACTS_BASE: '/contacts',
  CONTACTS_WITH_ID: id => `/contacts/${id}`,
  CONTACTS_BULK_DELETE: '/contacts/bulk-delete',
  CONTACTS_UPLOAD_CSV: '/contacts/upload-csv',
  CONTACTS_SEARCH: '/contacts/search',
  CONTACTS_SEARCH_NEW: '/contacts/search/new',
  CONTACTS_SEARCH_WEBSITE_LEADS: '/contacts/search/website-leads',
  CONTACTS_SEARCH_SALES_REPORT: '/contacts/search/sales-report',
  CONTACTS_PRINT_SALES_REPORT: '/contacts/search/sales-report/print',
  CONTACTS_TODAY: '/contacts/top-producer/today',
  CONTACTS_PIPELINE: '/contacts/top-producer/pipeline',
  CONTACTS_MONEY: '/contacts/top-producer/money',
  CONTACTS_TOP_PRODUCER_COUNTS: '/contacts/top-producer/counts',
  CONTACTS_TODAY_ADD: '/contacts/top-producer/add',
  CONTACTS_TODAY_REACHED: id => `/contacts/top-producer/${id}/reached`,
  CONTACTS_TODAY_NOTE: id => `/contacts/top-producer/${id}/note`,
  CONTACTS_TODAY_COMPLETE: id => `/contacts/top-producer/${id}/complete`,

  //inventory
  INVENTORY_BASE: '/inventory',
  INVENTORY_WITH_ID: id => `/inventory/${id}`,
  INVENTORY_DELETE_COMMENT: id => `/inventory/${id}/delete-comment`,

  INVENTORY_SEARCH: '/inventory/search',
  INVENTORY_SEARCH_NEW: '/inventory/search/new',
  INVENTORY_BROWSE: '/inventory/browse',
  INVENTORY_BROWSE_EXPORT: '/inventory/browse/export',

  //payrolls
  PAYROLLS_BASE: '/payroll',
  PAYROLLS_WITH_ID: id => `/payroll/${id}`,
  PAYROLLS_SEARCH: '/payroll/list',
  //files
  FILE: '/files',
  DISK_IMAGE_UPLOAD: '/files/disk-upload',
  DISK_VIDEO_UPLOAD: '/files/disk-upload-video',
  PUBLIC_IMAGE: fileName => `/files/public-images/${fileName}`,
  PUBLIC_VIDEO: fileName => `/files/public-videos/${fileName}`,
  DELETE_DISK_IMAGE: fileName => `/files/disk-images/${fileName}`,
  BASE64_FILE: '/files/base64',
  BASE64_FILE_WITH_NAME: name => `/files/${name}`,
  FILE_WITH_ID: id => `/files/${id}`,
  FILE_STREAM_WITH_ID: id => `/files/stream/${id}`,
  //file explorer
  FILE_EXPLORER_LIST: '/file-explorer/list',
  FILE_EXPLORER_FOLDER: '/file-explorer/folder',
  FILE_EXPLORER_FILE: '/file-explorer/file',
  FILE_EXPLORER_WITH_ID: id => `/file-explorer/${id}`,
  FILE_EXPLORER_DOWNLOAD: id => `/file-explorer/${id}/download`,
  //dochub
  DOCHUB_PRINT_TABLE: '/doc-hub/print-table',
  DOCHUB_PRINT_DOCUMENT_WITH_ID: id => `/doc-hub/print-document/${id}`,
  DOCHUB_PRINT_DOCUMENT_CANCEL: printSessionId => `/doc-hub/cancel-print-document/${printSessionId}`,
  DOCHUB_PRINT_DOCUMENT: `/doc-hub/print-document`,
  DOCHUB_DOCUMENT_PARAMS: id => `/doc-hub/document-params/${id}`,
  DOCHUB_DOCUMENT_LAYOUT: '/doc-hub/document-layout',
  DOCHUB_DOCUMENT_LAYOUT_SYNC: '/doc-hub/document-layout/sync',
  DOCHUB_DOCUMENT_LAYOUT_STATUS: '/doc-hub/document-layout/status',
  DOCHUB_PRINT_MHIT: '/doc-hub/print-mhit-report',
  DOCHUB_VARIABLES: '/doc-hub/variables',

  //misc
  APP_CHOICES_BASE: '/app-choices',
  APP_CHOICES_LIST: '/app-choices/list',
  FORM_FIELDS_LIST: '/form-fields/list',
  FORM_FIELDS_UPDATE_MANY: '/form-fields/update-many',
  ENTITY_DEFINITIONS_BASE: '/entity-definitions',
  ENTITY_DEFINITIONS_LIST: '/entity-definitions/list',
  ENTITY_DEFINITION_WITH_ID: id => `/entity-definitions/${id}`,
  ENTITY_DEFINITION_RECORDS: id => `/entity-definitions/${id}/records`,
  APP_CHOICES_WITH_ID: id => `/app-choices/${id}`,
  COMMISSION_SHEET_OPTIONS_BASE: '/commission-sheet-options',
  COMMISSION_SHEET_OPTIONS_LIST: '/commission-sheet-options/list',
  COMMISSION_SHEET_OPTIONS_UPSERT: '/commission-sheet-options/upsert',
  COMMISSION_SHEET_OPTIONS_WITH_ID: id => `/commission-sheet-options/${id}`,
  PNL_OPTIONS_BASE: '/pnl-options',
  PNL_OPTIONS_LIST: '/pnl-options/list',
  PNL_OPTIONS_UPSERT: '/pnl-options/upsert',
  PNL_OPTIONS_WITH_ID: id => `/pnl-options/${id}`,
  LOTSET_BASE: '/lot-set-options',

  // courses (training)
  COURSES_BASE: '/courses',
  COURSES_SEARCH: '/courses/search',
  COURSES_REORDER: '/courses/reorder',
  COURSE_WITH_ID: id => `/courses/${id}`,
  COURSE_MODULES_SEARCH: courseId => `/courses/${courseId}/modules/search`,
  COURSE_MODULES: courseId => `/courses/${courseId}/modules`,
  COURSE_MODULES_REORDER: courseId => `/courses/${courseId}/modules/reorder`,
  COURSE_LESSONS_REORDER: courseId => `/courses/${courseId}/lessons/reorder`,
  MODULE_WITH_ID: moduleId => `/courses/modules/${moduleId}`,
  COURSE_MODULE_LESSONS_SEARCH: moduleId => `/courses/modules/${moduleId}/lessons/search`,
  COURSE_MODULE_LESSONS: moduleId => `/courses/modules/${moduleId}/lessons`,
  LESSON_WITH_ID: lessonId => `/courses/lessons/${lessonId}`,
  COURSES_PENDING_COUNT: '/courses/progress/pending-count',
  COURSE_PROGRESS: courseId => `/courses/progress/${courseId}`,
  MODULE_PROGRESS: moduleId => `/courses/progress/modules/${moduleId}`,
  COURSE_ATTACHMENT_UPLOAD: courseId => `/courses/${courseId}/attachments`,
  COURSE_ATTACHMENT_STREAM: (courseId, fileId) => `/courses/${courseId}/attachments/${fileId}`,
  COURSE_ATTACHMENT_VIDEO_STREAM: (courseId, fileId) => `/courses/${courseId}/attachments/${fileId}/stream`,

  CONVERSATION_BANK_SEARCH: '/conversation-bank/search',
  CONVERSATION_BANK_FILTERS: '/conversation-bank/filters',
  CONVERSATION_BANK_USAGE: '/conversation-bank/usage',
  CONVERSATION_BANK_REORDER: '/conversation-bank/reorder',
  CONVERSATION_BANK_BASE: '/conversation-bank',
  CONVERSATION_BANK_WITH_ID: id => `/conversation-bank/${id}`,

  // server info (dev only)
  SERVER_INFO: '/server-info',
};

// Universal Search
export const UNIVERSAL_SEARCH_ROUTE = '/search';

//for client
export const serviceForm = {
  forms: [
    {
      name: 'Open A Service Request',
      markCompulsoryFields: true,
      compact: true,
      hideFormName: true,
      submit: {
        name: 'Request',
        show: false,
        onSubmit: 'onRequestFormSubmit'
      },
      rows: [
        {
          columns: [
            {
              default: 12,
              field: {
                type: 'custom-html',
                html: `<h6 class="text-muted">
                Please fill up the form below to request a new service.
              </h6><hr class='my-2'/>`
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 6,
              xs: 12,
              field: {
                id: 'name',
                title: 'Name',
                type: 'text',
                required: true
              }
            },
            {
              default: 6,
              xs: 12,
              field: {
                id: 'address',
                title: 'Address',
                type: 'text',
                required: true
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 6,
              xs: 12,
              field: {
                id: 'phoneNumber',
                title: 'Phone Number',
                type: 'text',
                required: true
              }
            },
            {
              default: 6,
              xs: 12,
              field: {
                id: 'emailAddress',
                title: 'Email Address',
                type: 'text',
                variant: 'email',
                required: true
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 6,
              xs: 12,
              field: {
                id: 'typeOfHome',
                title: 'Type of Home',
                type: 'dropdown',
                options: ['Single Wide', 'Double Wide', 'Tiny Home'],
                required: true
              }
            },
            {
              default: 6,
              xs: 12,
              field: {
                id: 'homeManufacturer',
                title: 'Home Manufacturer',
                type: 'dropdown',
                options: [
                  'Cavalier',
                  'Cavco',
                  'Clayton',
                  'Fleetwood',
                  'Jessup',
                  'Legacy',
                  'Southern Energy',
                  'TRU Homes'
                ],
                required: true
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 12,
              xs: 12,
              field: {
                id: 'serviceIssue',
                title: 'Service Issue',
                type: 'text-area',
                required: true
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 6,
              xs: 12,
              field: {
                id: 'locationOfProblem',
                title: 'Location of problem',
                type: 'checkbox-group',
                options: ['Kitchen', 'Living Room', 'Other'],
                required: true,
                boxWidth: 12,
                dependentElem: 'locationProblemOther',
                showDependentOn: 'Other'
              }
            },
            {
              default: 6,
              xs: 12,
              field: {
                id: 'type',
                title: 'Type',
                type: 'checkbox-group',
                options: ['Plumbing', 'Electrical', 'Other'],
                required: true,
                boxWidth: 12,
                dependentElem: 'typeOther',
                showDependentOn: 'Other'
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 6,
              xs: 12,
              field: {
                id: 'locationProblemOther',
                type: 'text',
                required: false,
                hint: 'Other problem here...',
                className: 'd-none'
              }
            },
            {
              default: 6,
              xs: 12,
              field: {
                id: 'typeOther',
                type: 'text',
                required: false,
                hint: 'Other type here...',
                className: 'd-none'
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 12,
              xs: 12,
              field: {
                id: 'salesPerson',
                title: 'Sales person',
                type: 'text',
                required: true
              }
            }
          ]
        },
        {
          columns: [
            {
              default: 12,
              xs: 12,
              field: {
                id: 'remarks',
                title: 'Remarks',
                type: 'text-area',
                required: true
              }
            }
          ]
        }
      ]
    }
  ]
};

// areKeysFixedOnDocument [when this is true only values are passed to api in array form ['a', '' ,'b']]
// because keys are already present on document
// when false, keys as well as value is passed to server in array of keyValue [{key:'', value: ''}]
// isTableInput, when this is true data received is array of arrays of length 2 but needs to be converted to array of keyValue
// isTableInput cannot be true is areKeysFixedOnDocument is true

export const keyValueFieldConfigs = [
  {
    name: 'optionalEquipmentAndAccessories',
    label: 'Optional Equipment And Accessories',
    isTableInput: true,
    defaultValue: 'Inc'
  },
  {
    name: 'twentyfirstImprovementFundsOptions',
    label: '21st Improvement Funds Options',
    isTableInput: true,
    defaultValue: 'Inc'
  },

  { name: 'retailerOptions', label: 'Retailer Options', isTableInput: true, defaultValue: 0 },

  {
    name: 'countryPlaceFhaPaImprovements',
    label: 'Country Place (FHA) PA Improvements',
    isTableInput: true,
    defaultValue: 'Inc'
  },
  {
    name: 'twentifirstImprovementInspectionOptions',
    label: '21st Improvement Inspection Options',
    isTableInput: true,
    defaultValue: ''
  },
  {
    name: 'tinyHomeAdditionalProducts',
    label: 'Tiny Home Additional Products',
    areKeysFixedOnDocument: true,
    defaultValue: 'Inc'
  },
  {
    name: 'sellerPaidOptions',
    label: 'Seller Paid Options',
    isMultiText: true
  },
  {
    name: 'promiseListItems',
    label: 'Promise List Items',
    isMultiText: true
  }
];

export const numbersOnlyFields = ['Cell Phone #', 'Cell Phone #2'];

//SERVICE ORDER CONSTANTS
export const roomOptions = [
  'Kitchen',
  'Master Bath',
  'Guest Bath',
  'Laundry Room',
  'Livingroom 1',
  'Livingroom 2',
  'Hallway',
  'Guest Bedroom 1',
  'Guest Bedroom 2',
  'Guest Bedroom 3',
  'Guest Bedroom 4'
];

export const wallOptions = ['Front', 'Back', 'Left Endwall', 'Right Endwall'];
export const closetOptions = [
  'Master Closet',
  'Guest Bedroom Closet 1',
  'Guest Bedroom Closet 2',
  'Guest Bedroom Closet 3',
  'Guest Bedroom Closet 4',
  'Guest Bedroom Closet 5'
];

// Key Names

export const KEY_NAMES = ['ctrl', 'alt'];

export const serviceOrderOptions = {
  Kitchen: {
    parts: [],
    location: ['Counter', 'Island', 'Backsplash', 'Floor', 'Ceiling', 'Cabinets', 'Electrical', 'Plumbing']
  },
  'Heating, Plumbing, Baths': {
    parts: ['Faucets', 'Shower', 'Vents', 'Toilet', 'Furnace'],
    location: roomOptions
  },
  'Ceiling-Structural-Fireplaces': {
    parts: ['Texture', 'Flat'],
    location: roomOptions
  },
  'Exterior Siding': {
    parts: ['Vinyl', 'Smart Panel', 'Hardi Panel', 'Metal', 'Stucco'],
    location: wallOptions
  },
  'Exterior Dormer': { parts: [], location: [] },
  'Exterior Shutters': {
    parts: [],
    location: ['Left End Wall', 'Right End Wall', 'A Half', 'B Half', 'SingleWide Front', 'SingleWide Rear']
  },
  'Exterior Windows': { parts: [], location: wallOptions },
  'Exterior Belly': {
    parts: ['Belly Tape', 'Belly Wrap', 'Stapled Plywood'],
    location: ['A Half', 'B Half', 'SingleWide']
  },
  'Exterior Roof': {
    parts: [
      'Ridge Vent',
      'Shingles',
      'Metal Flashing',
      'Tar Paper',
      'Vent-PVC',
      'Vent-Mushroom',
      'Vent-Bell',
      'Vent-Exhaust'
    ],
    location: ['Ridge', 'A Half', 'B Half', 'SingleWide']
  },
  'Interior Walls and Decor': {
    parts: ['T&T', 'Batten Strip', 'Wall Panels', 'Shelf', 'Miniblinds'],
    location: [...wallOptions, ...closetOptions]
  },
  'Doors and Windows': {
    parts: [
      'Door Jamb',
      'Storm Door',
      'Front Door',
      'Bank Door',
      'Sliding Glass Door',
      'Barn Door',
      'French Door',
      'Window',
      'Double Window',
      'Bathroom Window',
      'Kitchen Window'
    ],
    location: ['Front Door', 'Back Door', 'Pantry', ...roomOptions]
  },
  Cabinets: { parts: [], location: roomOptions },
  Mirrors: { parts: [], location: roomOptions },
  'Cieling & Door Trims': {
    parts: [
      'Ceiling Panel',
      'Marriage Ceiling Board',
      'Texture/Stomp',
      'Crown Moulding',
      'Ceiling Trim',
      'Door Trim',
      'Door Jamb',
      'Door Stop'
    ],
    location: []
  },
  'Counter Tops & Ceramic Colors': { parts: [], location: ['Kitchen', 'Master Bathroom', 'Guest Bathroom'] },
  'Carpet and Lino': {
    parts: ['Carpet', 'Carpet Pad', 'Lino', 'Transition Bar', 'Quarter Round Trim', 'Wood Filler(Lino to Lino)'],
    location: roomOptions
  },
  Appliances: {
    parts: [
      'Refrigerator',
      'Stove',
      'Dishwasher',
      'Microwave',
      'Hood Vent',
      'Furnace',
      'Air Conditioner',
      'Heat Pump',
      'Water Heater'
    ],
    location: ['Kitchen', 'Master Bath', 'Guest Bath', 'Laundry Room', 'Hallway']
  },
  Electrical: {
    parts: [
      'Chandelier',
      'Breaker Box',
      'Thermostat',
      'Ceiling Fan',
      'AC Disconnect Box',
      'Pendant Lights',
      'Can Lights',
      'Coach Lights',
      'Porch Light',
      'Exterior Light',
      'Dish Fixture',
      'Globe Fixture',
      'Gumball Fixture',
      'Pendant Fixture',
      'Frosted Tube Fixture',
      'Jar Fixture',
      'Switch Plate Single',
      'Switch Plate Double',
      'Switch Plate Triple',
      'Toggle Switch Single',
      'Toggle Switch Double',
      'GFI Outlet',
      'Outlet Box- Receptacle',
      'Outlet Face Plate',
      'Smoke Alarm',
      'Exterior Outlet'
    ],
    location: [...roomOptions, 'Outside Front Door', 'Outside Back Door']
  },
  Insulation: { parts: [], location: ['Floor', 'Exterior Walls', 'Ceiling', 'Underbelly'] },
  Flooring: { parts: [], location: [] },
  'Furniture/Decor': { parts: [], location: [] }
};

export const repairOptions = ['Replace', 'Repair', 'Install', 'Adjust', 'Remove and Replace'];
export const causeOptions = ['Missing', 'Transport', 'Defect', 'Exterior Transport Damage', 'Low Water Pressure'];
export const descOptions = [
  'Kitchen',
  'Heating, Plumbing, Baths',
  'Ceiling-Structural-Fireplaces',
  'Exterior Siding',
  'Exterior Dormer',
  'Exterior Shutters',
  'Exterior Windows',
  'Exterior Belly',
  'Exterior Roof',
  'Interior Walls and Decor',
  'Doors and Windows',
  'Cabinets',
  'Mirrors',
  'Cieling & Door Trims',
  'Counter Tops & Ceramic Colors',
  'Carpet and Lino',
  'Appliances',
  'Electrical',
  'Insulation',
  'Flooring',
  'Furniture/Decor'
];

//SEARCH RELATED
export const contactSearchPaths = [
  '_id',
  'buyer',
  'coBuyer',
  'buyerEmail',
  'propertyAddress',
  'zipCode',
  'cellPhone',
  'cellPhone2',
  'consultantsFollowUpNotes'
];

export const inventoryPaths = [
  '_id',
  'unitLocation',
  'manufacturer/name',
  'manufacturer/factoryShortName',
  'modelName',
  'model/modelName',
  'model/modelNumber',
  'serialA',
  'serialB',
  'bedrooms',
  'bathrooms',
  'sizeA',
  'sizeB',
  'hudA',
  'hudB',
  'floorplanLender/name',
  'lotSet',
  'typeOfUnit'
];

export const contractSearchPaths = [
  '_id',
  'buyer',
  'coBuyer',
  'salesLocation/location',
  'tech/name',
  'salesperson',
  'propertyAddress',
  'propertyCity',
  'propertyState',
  'zipCode',
  'propertyCounty',
  'cellPhone',
  'cellPhone2',
  'buyerEmail',
  'stateInspection',
  'serialA'
];

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const contractMilestoneDates = [
  {
    name: 'Setup',
    key: 'setup',
    start: 'dateSetUpStart',
    end: 'dateSetUpEnd',
    emailField: 'dateSetupEmails',
    options: 'setupNotificationOptions'
  },
  {
    name: 'Makeready',
    key: 'makeready',
    start: 'makereadyStartDate',
    end: 'makereadyEndDate',
    emailField: 'makereadyDateEmails',
    options: 'makereadyNotificationOptions'
  },
  {
    name: 'Delivery',
    key: 'delivery',
    start: 'dateDeliveredStart',
    end: 'dateDeliveredEnd',
    emailField: 'dateDeliveredEmails',
    options: 'deliveryNotificationOptions'
  },
  {
    name: 'Walkthrough',
    key: 'walkthrough',
    start: 'walkThroughDate',
    emailField: 'walkThroughDateEmails'
  },
  {
    name: 'Service',
    key: 'service',
    start: 'serviceStartDate',
    end: 'serviceEndDate',
    emailField: 'serviceScheduledDateEmails',
    options: 'serviceNotificationOptions'
  }
];

export const contractToggleOptions = ({ contract, appChoices }) => [
  {
    label: 'Status',
    key: 'status',
    variant: 'info',
    options: appChoices.find(c => c.key === 'Status').values.map(s => ({ option: s, value: s }))
  },
  {
    label: 'Bill-Back Status',
    key: 'billbackStatus',
    variant: 'primary',
    options: appChoices.find(c => c.key === 'Bill-Back Status').values.map(s => ({ option: s, value: s }))
  },
  {
    label: 'Funding Status',
    key: 'fundingStatus',
    variant: 'danger',
    options: appChoices.find(c => c.key === 'Funding Status').values.map(s => ({ option: s, value: s }))
  },
  {
    label: 'Lender',
    key: 'lender',
    variant: 'dark',
    options: appChoices
      .find(c => c.key === 'lender')
      .values.map(l => ({ option: l.lienholderCorporateName, value: l._id }))
  },
  ...(contract['lender'] && contract['lender'].lienholderCorporateName === 'Cash'
    ? [
      {
        label: 'Cash Paid',
        key: 'cashPaid',
        variant: 'dark',
        options: ['Yes', 'No'].map(s => ({ option: s, value: s }))
      }
    ]
    : []),
  {
    label: 'AC Status',
    key: 'acStatus',
    variant: 'primary',
    options: appChoices.find(c => c.key === 'AC Status').values.map(s => ({ option: s, value: s }))
  }
];

export const contractSearchFilterOptions = [
  { key: 'salesLocation', label: 'Sales Location', optionLabelKey: 'location' },
  { key: 'tech', label: 'Tech', isMembers: true, memberRole: 'Tech' },
  { key: 'status', label: 'Status', appChoiceKey: 'Status', includeBlankIfNull: true },
  { key: 'manufacturer', label: 'Manufacturer', optionLabelKey: 'factoryShortName' },
  { key: 'floorplanLender', label: 'Floorplan Lender', optionLabelKey: 'name' },
  { key: 'fundingStatus', label: 'Funding Status', appChoiceKey: 'Funding Status', includeBlankIfNull: true },
  { key: 'lender', label: 'Lender', optionLabelKey: 'lienholderCorporateName' },
  { key: 'acStatus', label: 'AC Status', appChoiceKey: 'AC Status', includeBlankIfNull: true }
];

export const contractSearchSortByOptions = [
  { name: 'Contract Updated Date', key: 'updatedAt' },
  { name: 'Setup Date', key: 'setup.start' },
  { name: 'Trimout Date', key: 'trimout.start' },
  { name: 'Service Scheduled Date', key: 'service.start' },
  { name: 'Date Form T Sent', key: 'dateFormTSent' },
  { name: 'Date Rebate Requested', key: 'dateRebateRequested' },
  { name: 'Date Rebate Received', key: 'dateRebateReceived' },
  { name: 'Date Rebate Reported', key: 'dateRebateReported' },
  { name: 'Date Signed', key: 'dateSigned' },
  { name: 'NPS Call Date', key: 'npsCallDate' }
];

//contacts
export const contactToggleOptions = ({ contact, appChoices }) => [
  {
    label: 'Status',
    key: 'status',
    variant: 'primary',
    options: appChoices.find(c => c.key === 'Contact Status').values.map(s => ({ option: s, value: s }))
  }

  // Needs to be removed

  // {
  //   label: 'Manufacturer',
  //   key: 'manufacturer',
  //   variant: 'primary',
  //   options: appChoices
  //     .find(c => c.key === 'manufacturer')
  //     .values.map(l => ({ option: l.factoryShortName, value: l._id }))
  // }
];

const contactSearchFilterOptions = [
  { key: 'typeOfUnit', label: 'Type Of Unit', appChoiceKey: 'Type Of Unit', includeBlankIfNull: true },
  { key: 'status', label: 'Contact Status', appChoiceKey: 'Contact Status', includeBlankIfNull: true },
  {
    key: 'newOrUsed',
    label: 'New/Used',
    options: ['New', 'Used'].map(o => ({ option: o, value: o })),
    includeBlankIfNull: true
  },
  {
    key: 'stockOrRso',
    label: 'Stock/RSO',
    options: ['Stock', 'RSO'].map(o => ({ option: o, value: o })),
    includeBlankIfNull: true
  },
  { key: 'lender', label: 'Lender', optionLabelKey: 'lienholderCorporateName' }
];

export const createFilterOptionsForContact = () => {
  if (userHasAnyRole(ROLE_SALES, ROLE_SALES_AND_INVENTORY_MANAGER)) {
    return contactSearchFilterOptions;
  }

  if (isAdmin()) {
    return [
      ...contactSearchFilterOptions,
      {
        key: 'contactOwner',
        label: 'Contact Owner',
        isMembers: true,
        memberRole: [
          ROLE_SUPER_ADMIN,
          ROLE_ADMIN,
          ROLE_SALES,
          ROLE_SALES_MANAGER,
          ROLE_SALES_AND_INVENTORY_MANAGER
        ],
        doNotIncludeUnassigned: false
      }
    ];
  }

  if (userHasAnyRole(ROLE_SALES_MANAGER)) {
    const loggedInUser = getProfileFromLocalStorage();
    return [
      ...contactSearchFilterOptions,
      {
        key: 'contactOwner',
        label: 'Contact Owner',
        options: [
          {
            option: `${loggedInUser.name}(me)`,
            value: loggedInUser._id
          },
          ...getSalesRepOfSalesManager().map(user => ({
            option: `${user.name}`,
            value: user._id
          }))
        ].sort((m1, m2) => m1.option.localeCompare(m2.option)),
        doNotIncludeUnassigned: true
      }
    ];
  }
  return [];
};

export const contactSearchSortByOptions = [
  { name: 'Follow Up Date', key: 'followUpDate' },
  { name: 'Initial Contact Date', key: 'initialContactDate' },
  { name: 'Last Contact Date', key: 'lastContactDate' },
  { name: 'Last Updated', key: 'updatedAt' },
  { name: 'Created Date', key: 'createdAt' }
];

export const inventorySearchFilterOptions = [
  { appChoiceKey: 'salesLocation', key: 'unitLocation', label: 'Unit Location', optionLabelKey: 'location' },
  { key: 'manufacturer', label: 'Manufacturer', optionLabelKey: 'factoryShortName' },
  { key: 'floorplanLender', label: 'Floorplan Lender', optionLabelKey: 'name' }
];

export const inventorySearchSortByOptions = [
  { name: 'Created Date', key: 'createdAt' },
  { name: 'Last Updated', key: 'updatedAt' },
  { name: 'Date of manufacture', key: 'dateOfManufacture' },
  { name: 'Invoice Date', key: 'invoiceDate' }
];

export const inventoryToggleOptions = ({ inventory, appChoices, includeManufacturer = true }) => {
  const manufacturer = includeManufacturer
    ? [
      {
        label: 'Manufacturer',
        key: 'manufacturer',
        variant: 'primary',
        options: appChoices
          .find(c => c.key === 'manufacturer')
          .values.map(l => ({ option: l.factoryShortName, value: l._id }))
      }
    ]
    : [];

  const sitesChoices = appChoices.find(choice => choice.key === 'salesLocation')?.values || [];

  const activeSiteChoice = sitesChoices.find(choice => choice?._id === inventory?.unitLocation?._id) || {};

  const slotsOptions = activeSiteChoice.sites
    ? activeSiteChoice.sites.flatMap(site => {
      return [
        {
          isTitle: true,
          title: site.name
        },
        ...site.slots
          .filter(s => !s.isOfficeBuilding)
          .map(s => ({
            option: s.number,
            value: s._id
          }))
      ];
    })
    : [];

  return [
    ...manufacturer,
    {
      label: 'Assigned Slot',
      key: 'currentSlotId',
      variant: 'primary',
      options: slotsOptions
    }
  ];
};

export const serviceSearchFilterOptions = [
  {
    key: 'status',
    label: 'Status',
    options: ['Pending', 'Claimed', 'Resolved'].map(s => ({
      option: s,
      value: s
    })),
    includeBlankIfNull: true
  }
];

export const myClaimsSearchFilterOptions = [
  {
    key: 'status',
    label: 'Status',
    options: ['Claimed', 'Resolved'].map(s => ({
      option: s,
      value: s
    })),
    includeBlankIfNull: true
  }
];

export const newServiceSearchSortByOptions = [
  { name: 'Last Updated', key: 'updatedAt' },
  { name: 'Request Date', key: 'createdAt' }
];

export const serviceSearchSortByOptions = [
  ...newServiceSearchSortByOptions,
  { name: 'Resolved Date', key: 'resolvedDate' }
];

export const installerFilterOptions = [
  { key: 'salesLocation', label: 'Sales Location', optionLabelKey: 'location' },
  { key: 'status', label: 'Status', appChoiceKey: 'Status' }
];

export const installerSortByOptions = [
  { name: 'Not Installed First', key: 'notInstalledFirst' },
  { name: 'Contract Updated Date', key: 'updatedAt' },
  { name: 'Setup Date', key: 'setup.start' },
  { name: 'Trimout Date', key: 'trimout.start' },
  { name: 'Service Scheduled Date', key: 'service.start' }
];

export const opsFilterOptions = installerFilterOptions;

export const opsSortByOptions = [
  { name: 'No Walkthrough First', key: 'noWalkthoughFirst' },
  { name: 'Contract Updated Date', key: 'updatedAt' },
  { name: 'Setup Date', key: 'setup.start' },
  { name: 'Trimout Date', key: 'trimout.start' },
  { name: 'Service Scheduled Date', key: 'service.start' }
];

export const typeOfTransactionOptions = [
  'Personal | New',
  'Personal | Used',
  'Personal | Lien Assignment',
  'Personal | Convert back to personal property',
  'Personal | Other',
  'Real | New',
  'Real | Used',
  'Real | Update',
  'Real | Other'
];

export const docHubSourceOptions = ['Contract', 'Contact', 'Inventory'];

export const commissionImportantFieldHTML = `
  <h6>Calculated Values: </h6>
  <div class='border border-dark p-2 rounded bg-light'>
  <div class='row text-dark midFont'>
      <div class='col col-12 col-md-4 mb-1'>
        Net Commission: <b id='netCommission'>$N/A</b>
      </div>
       <div class='col col-12 col-md-4 mb-1'>
      Total of Options: <b id='totalOfOptions'>$N/A</b>
      </div>
      <div class='col col-12 col-md-4 mb-1'>
      Gross Profit: <b id='grossProfit'>$N/A</b>
      </div>
      <div class='col col-12 col-md-4 mb-1'>
      Commission Due: <b id='commissionDue'>$N/A</b>
      </div>
       <div class='col col-12 col-md-4 mb-1'>
      Commission Due with Bonus: <b id='commissionDueWithBonus'>$N/A</b>
      </div>
       <div class='col col-12 col-md-4 mb-1'>
      Front End: <b id='frontEnd'>$N/A</b>
      </div>
      <div class='col col-12 col-md-4 mb-1'>
      Back End: <b id='backEnd'>$N/A</b>
      </div>
       <div class='col col-12 col-md-4 mb-1'>
      Balance to be Paid: <b id='balanceToBePaid'>$N/A</b>
      </div>
       <div class='col col-12 col-md-4 mb-1'>
      Balance after Advance: <b id='balanceAfterAdvance'>$N/A</b>
      </div>
      
      ${isMicasa
    ? `
        <div class='col col-12 col-md-4 mb-1'>
      <span id='bonusTypeName'>Bonus</span>: <b id='bonusValue'>$N/A</b>
      </div>
      <div class='col col-12 col-md-4 mb-1'>
      Total w/Bonus: <b id='totalWithBonusValue'>$N/A</b>
      </div>
        `
    : ''
  }
     
  </div>
  </div>`;

export const salesImportantFieldHTML = `
<h6>Calculated Values: </h6>
<div class='border border-dark p-2 rounded bg-light'>
<div class='row text-dark midFont'>
    <div class='col col-12 col-md-4 mb-1'>
    Options: <b id='options'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Sub-Total: <b id='subTotal'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Trade-In Net Allowance: <b id='tradeInNetAllowance'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Total Down Payment: <b id='totalDownPayment'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Unpaid Cash Price: <b id='unpaidCashPrice'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Total Unpaid Balance: <b id='totalUnpaidBalance'>$N/A</b>
    </div>
    <div class='col col-12 col-md-4 mb-1'>
    Total Balance Due / Amount Financed: <b id='totalBalanceDue'>$N/A</b>
    </div>
</div>
</div>`;

export const appChoicesKeys = [
  '21st Improvement Funds Options',
  '21st Improvement Inspection Options',
  'Admin Fee',
  'AC Status',
  'Bill-Back Status',
  'Contact Status',
  'Country Place (FHA) PA Improvements',
  'Delivery Notification Options',
  'Delivery/Set Up',
  'Exterior Siding',
  'Floorplan',
  'Funding Status',
  'Lead Source',
  'Makeready Notification Options',
  'New A/C',
  'Optional Equipment And Accessories',
  'Retailer Options',
  'Seller Paid Options',
  'Promise List Items',
  'Payroll Categories',
  'Rebate Eligible',
  'Setup Notification Options',
  'Service Notification Options',
  'Status',
  'Steps',
  'Stock/Lot Set/RSO',
  'Tech Status',
  'Tiny Home Additional Products',
  'Type',
  'Type Of Unit',
  'Utilities',
  'Walkthrough Status',
  'Print Groups Categories',
  'Special Instruction Options',
  'Loan Type'
];

export const withDefaultValuesKeys = [];

export const hasDefaulValues = key => withDefaultValuesKeys.includes(key);

export const additionalDocHubfieldTypes = ['text', 'number', 'date', 'dropdown', 'checkbox', 'checkbox-group'];
export const additionalDocHubfieldTypesWithDropdowns = ['dropdown', 'checkbox-group'];

export const RETURN_PATHS = {
  CONTRACT: ['_id', 'buyer', 'status', 'salesLocation', 'inventoryUnit'],
  CONTACT: ['_id', 'buyer', 'status', 'salesLocation', 'exportedContract'],
  INVENTORY: ['_id', 'unitLocation', 'serialA', 'serialB', 'model', 'hudA', 'hudB', 'isRSO', 'isStockInOrder']
};

export const defaultSitesLatLong = { lat: 31.9686, lng: -99.9018 };

export const pnlOptions = [
  { label: 'Del & set', key: 'Delivery/Set Up' },
  { label: 'Air Condition', key: 'Air Condition' },
  { label: 'Pad', key: 'Skirting' },
  { label: 'Concessions', key: 'Utilities' },
  { label: 'Team Leader', key: 'Team Leader' },
  { label: 'Admin', key: 'Admin' },
  { label: 'Service', key: 'Service' },
  { label: 'Bank fees', key: 'Bank Fees' },
  { label: 'Frg. Reim', key: 'Freight Reimbursement' },
  { label: 'NPS', key: 'NPS' },
  { label: 'Doc Prep', key: 'Doc Prep' },
  { label: 'Rebate', key: 'pnlRebate' }
];

export const expenseFields = [
  { isPnLOption: true, key: 'Doc Prep' },
  { isPnLOption: true, key: 'Freight Reimbursement' },
  { isPnLOption: true, key: 'NPS' },
  { key: 'pnlRebate', valueGetter: calculateRebate },
  { key: 'SP/Com', valueGetter: c => Number(c.salesPrice || 0) - 499 }
];

export const incomeFields = status => [
  {
    label: 'Invoice',
    key: 'invoice',
    valueGetter: c => (status === 'Contracted' ? c?.inventoryUnit?.invoice : c?.inventoryUnit[0]?.invoice)
  },
  { label: 'Del & set', isPnLOption: true, key: 'Delivery/Set Up' },
  { label: 'Air Condition', isPnLOption: true, key: 'Air Condition' },
  { label: 'Pad', isPnLOption: true, key: 'Skirting' },
  { label: 'Concessions', isPnLOption: true, key: 'Utilities' },
  {
    label: 'Commission Due',
    key: 'Commission Due',
    valueGetter: c => calculateCommissionDue({ ...c, newStatus: status })
  },
  { label: 'Team Leader', isPnLOption: true, key: 'Team Leader' },
  { label: 'Admin', isPnLOption: true, key: 'Admin' },
  { label: 'Service', isPnLOption: true, key: 'Service' },
  { label: 'Bank fees', isPnLOption: true, key: 'Bank Fees' }
];

export const staticFields = status => [
  { label: 'Buyer', value: contract => splitFullName(contract.buyer) || '-' },
  { label: 'Salesperson', value: contract => contract.salesperson || '-' },
  { label: 'Status', value: () => status },
  { label: 'Funding Status', value: contract => contract.fundingStatus || '-' },
  {
    label: 'Size Overall',
    value: contract =>
      (status === 'Contracted' ? contract.inventoryUnit?.sizeOverall : contract.inventoryUnit[0]?.sizeOverall) || '-'
  },
  {
    label: 'Invoice',
    value: contract =>
      status === 'Contracted' ? contract.inventoryUnit?.invoice : contract.inventoryUnit[0]?.invoice || '-'
  },
  {
    label: 'Commission Due',
    value: contract => calculateCommissionDue({ ...contract, newStatus: status })
  },
  {
    label: 'SP/Comm',
    value: contract => Number(contract.salesPrice || 0) - 499
  },
  { label: 'Bank', value: contract => contract.lender?.shortName || '-' },
  {
    label: 'Factory',
    value: contract =>
      (status === 'Contracted'
        ? contract.inventoryUnit?.manufacturer?.name
        : contract.inventoryUnit[0]?.manufacturer?.name) || '-'
  },
  {
    label: 'Date of Deposit',
    value: contract => (contract.dateOfDeposit ? moment(contract.dateOfDeposit).format('MMMM Do YYYY') : '-')
  }
];

// Site Improvement Allowances - Calculation Worksheet (Single Wide / Double-Triple Wide)
export const SITE_IMPROVEMENT_ALLOWANCES_SINGLE_WIDE = [
  { item: 'Air Conditioner', standard: 4500, max: 7000 },
  { item: 'Electric Hookups', standard: 2500, max: 3500 },
  { item: 'Water Hookups', standard: 750, max: 1500 },
  { item: 'Sewer Hookups', standard: 750, max: 1500 },
  { item: 'Gas Hookups', standard: 750, max: 1500 },
  { item: 'Steps & Decks (max 3 sets)', standard: 1000, max: 4000 },
  { item: 'Gutters & Downspouts', standard: 1300, max: 1800 },
  { item: 'Delivery & Set', standard: 4500, max: 7500 },
  { item: 'Trim Out', standard: 1500, max: 2500 },
  { section: 'Underpinning Skirting' },
  { item: 'Vinyl', standard: 2000, max: 2000, indent: true, highlight: true },
  { item: 'Metal', standard: 3000, max: 3000, indent: true, highlight: true },
  { item: 'Hardie Board/Insulated', standard: 4000, max: 4000, indent: true, highlight: true },
  { item: 'Masonry', standard: 6000, max: 6000, indent: true, highlight: true },
  { item: 'Footers', standard: 2500, max: 4000 },
  { item: 'Pad', standard: 2500, max: 4000 }
];

export const SITE_IMPROVEMENT_ALLOWANCES_DOUBLE_TRIPLE_WIDE = [
  { item: 'Air Conditioner', standard: 4500, max: 7000 },
  { item: 'Electric Hookups', standard: 2500, max: 3500 },
  { item: 'Water Hookups', standard: 750, max: 1500 },
  { item: 'Sewer Hookups', standard: 750, max: 1500 },
  { item: 'Gas Hookups', standard: 750, max: 1500 },
  { item: 'Steps & Decks (max 3 sets)', standard: 1000, max: 4000 },
  { item: 'Gutters & Downspouts', standard: 1300, max: 1800 },
  { item: 'Delivery & Set', standard: 9000, max: 14000 },
  { item: 'Trim Out', standard: 2000, max: 3500 },
  { section: 'Underpinning Skirting' },
  { item: 'Vinyl', standard: 2000, max: 2000, indent: true, highlight: true },
  { item: 'Metal', standard: 3000, max: 3000, indent: true, highlight: true },
  { item: 'Hardie Board/Insulated', standard: 4000, max: 4000, indent: true, highlight: true },
  { item: 'Masonry', standard: 6000, max: 6000, indent: true, highlight: true },
  { item: 'Footers', standard: 5000, max: 8000 },
  { item: 'Pad', standard: 5000, max: 8000 }
];

export const SITE_IMPROVEMENT_ALLOWANCES_FOOTNOTE =
  '* Steps & Decks: Allowances shown are per set. Maximum 3 sets allowed. Total allowance: $3,000 for standard ($1,000 × 3) or $12,000 for maximum ($4,000 × 3).';